import {
    createRef,
	RefObject,
	useCallback, 
	useEffect, 
	useMemo, 
	useRef, 
	useState
} from "react";
import {
	Grid,
    Box,
    SxProps,
    Theme,
    useTheme,
    useMediaQuery,
    Menu,
    MenuItem,
} from "@mui/material";
import BaseModel from "../models/_BaseModel";
import { ApiRequestType, DataListPropertyType, DefaultMenuTypes, NotificationType } from "../utils/Types";
import '../../src/styles/fonts/inter/inter.css';
import '../../src/styles/all.css';
import { Info as InfoIcon } from '@mui/icons-material';
import { dataCountPerRowLg, dataCountPerRowMd, dataCountPerRowSm, dataCountPerRowXl, footerHeight, headerHeight, pageTabHeight, pageToolbarHeight, toQueryString } from "../utils/Common";
import { useStateContext } from "../reducers";
import ListItem from "./ListItem";

const rowCount = 6;
const rowCountXl = 8;

function DataList<T extends BaseModel>(props: DataListPropertyType<T>) {
    const { dataType, setError, setLoading, actions, apiCaller, endpoint, apiMethod, colCountInRow, maxRowInPage, titleColumnName, details, needReload = false, keyField, filters, sort, pageNumber = 1, onSetPageCount } = props;
    const [data, setData] = useState<T[]>([]);
    const [dataRefList, setDataRefList] = useState<{id: any, ref: RefObject<T>}[]>([]);
    const [anchorEl, setAnchorEl] = useState<any>(null);
    const [openMenuButton, setOpenMenuButton] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const {menuState} = useStateContext();
    const gridContainerRef = useRef<HTMLDivElement>(null);
    const selectedDataRef = useRef<T | null>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isXl = useMediaQuery(theme.breakpoints.up('xl'));
    const isLg = useMediaQuery(theme.breakpoints.between('lg', 'xl'));
    const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));
    const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));
    const [take, setTake] = useState<number>(((isXs || isSm) ? dataCountPerRowSm : (isMd ? dataCountPerRowMd : (isLg ? dataCountPerRowLg : dataCountPerRowXl))) * (isXl || isSm || isXs ? rowCountXl : rowCount));

    useEffect(() => {
        if (isXs || isSm)
        {
            setTake(dataCountPerRowSm * rowCountXl);
            return;
        }
        
        if (isMd)
        {
            setTake(dataCountPerRowMd * rowCount);
            return;
        }
        
        if (isLg)
        {
            setTake(dataCountPerRowLg * rowCount);
            return;
        }

        if (isXl)
        {
            setTake(dataCountPerRowXl * rowCountXl);
            return;
        }
    }, [isXl, isLg, isMd, isSm, isXs]);
  
    const handleMenuButtonClick = (event: any, data: T) => {
        selectedDataRef.current = data;
        setAnchorEl(event.currentTarget);
        setOpenMenuButton(true);
    };
  
    const handleMenuButtonClose = () => {
        setOpenMenuButton(false);
        selectedDataRef.current = null;
    };

    const handleMenuItemClick = (actionId: DefaultMenuTypes | string) => {
        if (!selectedDataRef?.current)
            return;

        const currentAction = actions?.filter(a => a.id === actionId)[0];

        if (!currentAction)
            return;

        currentAction.action(selectedDataRef.current);
    };

    const loadData = useCallback(() => {
        setIsLoading(true);

        if (!apiCaller || !endpoint)
        {
            setIsLoading(false);
            return;
        }

        const requestMethod: ApiRequestType = apiMethod ?? "get";
        const filtersString = filters?.length ? toQueryString(filters) : null;
        const sortString = sort ? toQueryString([sort]) : null;

        const apiCallFunc = requestMethod === "get" ? apiCaller.get(`${endpoint}${`?skip=${(pageNumber - 1) * take}&take=${take}${filtersString ? `&${filtersString}` : ""}${sortString ? `&${sortString}` : ""}`}`) : (requestMethod === "post" ? apiCaller.post(`${endpoint}${filtersString ? `?${filtersString}` : ""}`) : null);

        if (!apiCallFunc)
        {
            setIsLoading(false);
            return;
        }

        apiCallFunc
            .then((response) => {
                const newData: T[] = response.data.data;
                setData([...newData]);

                if (onSetPageCount)
                    onSetPageCount(response.data?.total ? Math.ceil(response.data?.total / take) : 1);
            })
            .catch((error) => {
                const errorObj: NotificationType = {
                    title: error.response?.statusText && error.response?.statusText !== "" ? error.response?.statusText : "Error!",
                    message: error?.response?.message ?? error?.response?.data ?? "system error.",
                }

                if (setError)
                    setError(errorObj);
            })
            .finally(() => setIsLoading(false))
        ;
    }, [apiCaller, pageNumber, take, endpoint, apiMethod, setError, maxRowInPage, dataType, filters, sort, onSetPageCount]);

    useEffect(() => {
        if (!needReload)
            return;

        loadData();
    }, [needReload]);

    useEffect(() => {
        loadData();
    }, [pageNumber, take]);

    useEffect(() => {
        if (!data?.length)
            return;

        setDataRefList([
            ...data.map(d => {
                return {
                    id: (d as any)[keyField],
                    ref: createRef<T>(),
                };
            })
        ])
    }, [data, keyField]);

    useEffect(() => {
        setLoading(isLoading);
    }, [isLoading]);

    const boxStyle = useMemo<SxProps<Theme>>(() => {
        const height = `calc(100dvh - ${headerHeight}px - ${footerHeight}px - ${pageTabHeight}px - ${isMobile ? pageTabHeight : 0}px - ${pageToolbarHeight}px)`;
        const heightUnsupported = `calc(100vh - ${headerHeight}px - ${footerHeight}px - ${pageTabHeight}px - ${isMobile ? pageTabHeight : 0}px - ${pageToolbarHeight}px)`;
        const paddingY = "1rem";
        const paddingX = "1.5rem";

        return data.length ?
            {
                display: "flex",
                justifyContent: "center",
                height: heightUnsupported,
                '@supports (height: 100dvh)': {
                    height,
                },
                width: "100%",
                overflowY: "auto",
                paddingY,
                paddingX,
            }
            :
            {
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: heightUnsupported,
                '@supports (height: 100dvh)': {
                    height,
                },
                width: "100%",
                paddingY,
                paddingX,
            };
    }, [data, isMobile, menuState?.showSidebar]);
    
    return (
        <>
            <Box
                sx={{...boxStyle}}
                ref={gridContainerRef}
            >
            {
                data?.length && dataRefList?.length ?
                <Grid container spacing={2} alignItems="flex-start" alignContent="flex-start" justifyContent="flex-start">
                {
                    data.map((d, idx) => {
                        const currentRefItem = dataRefList.filter(r => r.id === (d as any)[keyField])[0];

                        return (
                            <ListItem<T>
                                data={d}
                                keyIdx={idx}
                                colCountInRow={colCountInRow}
                                details={details}
                                ref={currentRefItem?.ref}
                                titleColumnName={titleColumnName}
                                openMenuButton={openMenuButton}
                                onMenuButtonClick={(selectedData: T | undefined, event: any) => handleMenuButtonClick(event, selectedData as T)}
                                onMenuButtonClose={handleMenuButtonClose}
                                onMenuItemClick={(actionId?: DefaultMenuTypes | string) => handleMenuItemClick(actionId as DefaultMenuTypes | string)}
                                actionExists={(actions?.length ?? 0) > 0}
                            />
                        );
                    })
                }
                </Grid>
                :
                (
                    !isLoading ?
                    <Box sx={{color: "gray", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center"}}>
                        <InfoIcon fontSize="large" />
                        <h2>Data tidak ditemukan.</h2>
                    </Box>
                    :
                    null
                )
            }
            </Box>
        {
            openMenuButton && selectedDataRef?.current ?
            <Menu
                id="dropdown-menu"
                anchorEl={anchorEl}
                open={openMenuButton}
                onClose={handleMenuButtonClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                    sx: {
                        backgroundColor: "#fff",
                    },
                }}
                sx={{
                    "& > .MuiPaper-root": {
                        boxShadow: "none",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                    },
                }}
            >
            {
                actions?.map((a, a_idx) => (
                    <MenuItem
                        key={`data_cell_action_${a_idx}`}
                        onClick={() => {
                            a.action(selectedDataRef?.current as T);
                            handleMenuButtonClose();
                        }}
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "start",
                            alignItems: "center",
                            boxShadow: "none",
                            borderRadius: 0,
                            borderRight: "1px solid rgba(255, 255, 255, 0.5)",
                            fontSize: "0.9rem",
                        }}
                        disabled={a.disabled ? a.disabled(selectedDataRef?.current ?? undefined) : false}
                        className="text-bg-grid-normal"
                    >
                    {
                        a.icon ?
                        <Box
                            sx={{
                                border: "1px solid #444",
                                padding: "4px",
                                minWidth: "2.2rem",
                                minHeight: "2.2rem",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginRight: "8px",
                                borderRadius: "0.4rem",
                            }}
                        >
                            <a.icon
                                fontSize="small"
                                sx={{
                                    fontSize: "0.9rem",
                                    // color: "#2196F3",
                                }}
                            />
                        </Box>
                        :
                        null
                    }
                    {
                        a.title
                    }
                    </MenuItem>
                ))
            }
            </Menu>
            :
            null
        }
        </>
    );
};

DataList.defaultProps = {
    useDummyData: false,
};

export default DataList;
